import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import * as teamActions from "../actions/teamsActions";

export const searchTeams = createAsyncThunk(
  "teams/searchTeams",
  async (params, { rejectWithValue }) => {
    try {
      const data = await teamActions.searchTeams(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCustomTeam = createAsyncThunk(
  "teams/createCustomTeam",
  async ({ title, email, sport }, { rejectWithValue }) => {
    try {
      const data = await teamActions.createCustomTeam({ title, email, sport });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTeam = createAsyncThunk(
  "teams/updateTeam",
  async ({ teamId, teamData }, { rejectWithValue }) => {
    try {
      const data = await teamActions.updateTeam(teamId, teamData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTeam = createAsyncThunk(
  "teams/deleteTeam",
  async (
    { tournamentId, categoryId, teamId },
    { rejectWithValue }
  ) => {
    try {
      await teamActions.deleteTeam(tournamentId, categoryId, teamId);
      return teamId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchAllTeams = createAsyncThunk(
  "teams/fetchAllTeams",
  async (params, { rejectWithValue }) => {
    try {
      const response = await teamActions.getAllTeams(params);
      return response.results || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch teams");
    }
  }
);

export const fetchTeamById = createAsyncThunk(
  "teams/fetchTeamById",
  async (teamId, { rejectWithValue }) => {
    try {
      const data = await teamActions.getTeamById(teamId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch team details");
    }
  }
);

export const fetchTeamStats = createAsyncThunk(
  "teams/fetchTeamStats",
  async (teamId, { rejectWithValue }) => {
    try {
      const data = await teamActions.getTeamStats(teamId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch team stats");
    }
  }
);

export const fetchTeamOverview = createAsyncThunk(
  "teams/fetchTeamOverview",
  async (
    { teamId, tournamentId, categoryId },
    { rejectWithValue }
  ) => {
    try {
      const data = await teamActions.getTeamOverview(teamId, {
        tournamentId,
        categoryId,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch team overview");
    }
  }
);

const teamsSlice = createSlice({
  name: "teams",
  initialState: {
    teams: [],
    allTeams: [],
    searchResults: [],
    selectedTeam: null,
    loading: false,
    error: null,
    inviteStatus: null,
    isSearching: false,
    searchError: null,
    currentTeam: null,
    currentTeamLoading: false,
    teamStats: null,
    teamStatsLoading: false,
    teamStatsError: null,
    teamOverview: null,
    teamOverviewLoading: false,
    teamOverviewError: null,
  },
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    setSelectedTeam: (state, action) => {
      state.selectedTeam = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearInviteStatus: (state) => {
      state.inviteStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Search Teams
      .addCase(searchTeams.pending, (state) => {
        state.isSearching = true;
        state.searchError = null;
      })
      .addCase(searchTeams.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload.results.map((team) => ({
          id: team._id || team.id,
          name: team.name || team.title,
          type: team.type,
          _id: team._id || team.id,
          title: team.name || team.title,
          crest: team.crest || team.image || '',
        }));
        state.searchError = null;
      })
      .addCase(searchTeams.rejected, (state, action) => {
        state.isSearching = false;
        state.searchError = action.payload;
        state.searchResults = [];
      })
      // Update Team
      .addCase(updateTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.teams.findIndex(
          (team) => team._id === action.payload._id
        );
        if (index !== -1) {
          state.teams[index] = action.payload;
        }
      })
      .addCase(updateTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Team
      .addCase(deleteTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        state.loading = false;
        state.teams = state.teams.filter((team) => team.id !== action.payload);
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch All Teams
      .addCase(fetchAllTeams.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTeams.fulfilled, (state, action) => {
        state.loading = false;
        state.allTeams = action.payload;
      })
      .addCase(fetchAllTeams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Team by ID
      .addCase(fetchTeamById.pending, (state) => {
        state.currentTeamLoading = true;
        state.error = null;
      })
      .addCase(fetchTeamById.fulfilled, (state, action) => {
        state.currentTeamLoading = false;
        state.currentTeam = action.payload;
      })
      .addCase(fetchTeamById.rejected, (state, action) => {
        state.currentTeamLoading = false;
        state.error = action.payload;
      })
      // Fetch Team Stats
      .addCase(fetchTeamStats.pending, (state) => {
        state.teamStatsLoading = true;
        state.teamStatsError = null;
      })
      .addCase(fetchTeamStats.fulfilled, (state, action) => {
        state.teamStatsLoading = false;
        state.teamStats = action.payload;
      })
      .addCase(fetchTeamStats.rejected, (state, action) => {
        state.teamStatsLoading = false;
        state.teamStatsError = action.payload;
      })
      // Fetch Team Overview
      .addCase(fetchTeamOverview.pending, (state) => {
        state.teamOverviewLoading = true;
        state.teamOverviewError = null;
      })
      .addCase(fetchTeamOverview.fulfilled, (state, action) => {
        state.teamOverviewLoading = false;
        state.teamOverview = action.payload;
      })
      .addCase(fetchTeamOverview.rejected, (state, action) => {
        state.teamOverviewLoading = false;
        state.teamOverviewError = action.payload;
      });
  },
});

export const {
  clearSearchResults,
  setSelectedTeam,
  clearError,
  clearInviteStatus,
} = teamsSlice.actions;

export default teamsSlice.reducer;
